/**
 * RTA (real-time analyzer) geometry — the room's frequency response reshaped
 * into fractional-octave bars instead of a continuous curve. This is the
 * standard "how much energy is in this band" view acoustic measurement tools
 * (REW, Smaart) use alongside the continuous FR curve: easier to read at a
 * glance, and it's what a 1/3-octave RTA meter on a hardware analyzer shows.
 *
 * Band edges use the base-2 fractional-octave system (center = 1000·2^(x/b)),
 * not the ANSI S1.11 base-10 "preferred numbers" table — acoustically
 * equivalent (same octave ratio), just not snapped to the traditional
 * 31.5/63/125 Hz labels. Each bar's level is the ENERGY (power) mean of every
 * fine-grid dB point whose frequency falls in the band, per the standard
 * convention for combining SPL values — never an arithmetic mean of dB.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, logFreqToX, type LinearScale, type LogFreqScale } from './scales';
import { dbViewportFromSeries, energyMeanDb, magToDb } from './series';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';

export type OctaveFraction = 1 | 3 | 6 | 12;

/** Fractional-octave band center frequencies covering [fMin, fMax], base-2. */
export function computeOctaveBandCenters(fMin: number, fMax: number, bandsPerOctave: OctaveFraction): number[] {
  if (!(fMax > fMin) || fMin <= 0) return [];
  const xLo = Math.ceil(bandsPerOctave * Math.log2(fMin / 1000));
  const xHi = Math.floor(bandsPerOctave * Math.log2(fMax / 1000));
  const centers: number[] = [];
  for (let x = xLo; x <= xHi; x++) centers.push(1000 * 2 ** (x / bandsPerOctave));
  return centers;
}

export interface RtaBar {
  centerHz: number;
  loHz: number;
  hiHz: number;
  /** Energy-averaged dB across fine points in [loHz, hiHz). Null = no data
   *  fell in this band (renders as an empty gap, not a zero-height bar). */
  db: number | null;
  x0: number;
  x1: number;
  /** Plot-local y of the bar's top; bar spans [y, frame.plotH]. Equals
   *  frame.plotH (zero height) when db is null. */
  y: number;
}

export interface RtaInput {
  freqs: number[];
  mag: number[];
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  bandsPerOctave?: OctaveFraction;
  measureWidth?: (label: string) => number;
}

export interface RtaGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  bandsPerOctave: OctaveFraction;
  xScale: LogFreqScale;
  yScale: LinearScale;
  yDomain: { lo: number; hi: number };
  bars: RtaBar[];
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeRtaGeometry(opts: RtaInput): RtaGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const bandsPerOctave = opts.bandsPerOctave ?? 3;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  if (!opts.freqs.length || !opts.mag.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, bandsPerOctave, xScale,
      yScale: createLinearScale(frame.plotH, 0, 15, true),
      yDomain: { lo: 0, hi: 15 },
      bars: [], xTicks, yTicks: [],
    };
  }

  const dB = magToDb(opts.mag);
  const centers = computeOctaveBandCenters(fMin, fMax, bandsPerOctave);
  const edgeRatio = 2 ** (1 / (2 * bandsPerOctave));

  const bandDbValues = centers.map((center) => {
    const lo = center / edgeRatio;
    const hi = center * edgeRatio;
    const inBand: number[] = [];
    opts.freqs.forEach((f, i) => {
      if (f >= lo && f < hi) inBand.push(dB[i]!);
    });
    return energyMeanDb(inBand);
  });

  const finiteDb = bandDbValues.filter((v): v is number => v != null);
  const yDomain = finiteDb.length ? dbViewportFromSeries([finiteDb]) : { lo: 0, hi: 15 };
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const bars: RtaBar[] = centers.map((center, i) => {
    const lo = center / edgeRatio;
    const hi = center * edgeRatio;
    const db = bandDbValues[i]!;
    const x0 = logFreqToX(xScale, Math.max(lo, fMin));
    const x1 = logFreqToX(xScale, Math.min(hi, fMax));
    const y = db != null ? linearToPlot(yScale, db) : frame.plotH;
    return { centerHz: center, loHz: lo, hiHz: hi, db, x0, x1, y };
  });

  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, 5).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: Math.round(v) % 10 === 0, label: `${Math.round(v)}`,
  }));

  return { empty: false, frame, fMin, fMax, bandsPerOctave, xScale, yScale, yDomain, bars, xTicks, yTicks };
}
